// Resolve the dashboard's string robot key (Robot.name) to robot-hub's int
// Robot.id, which is what the hub's filter/foreign-key columns use.
//
// Fast path: if /fleet/inventory already populated `hubId` on the fleet entry,
// return that without a fetch. Cloud users hit this path.
//
// Slow path: fetch /org/{orgId}/robots and cache per-org. Kiosk hits this path
// because /kiosk/info doesn't carry the hub-side int id.
//
// Cache lifetime is per-tab-load: failures (offline, 401) cache `null` for the
// rest of the session so we don't hammer the network. Callers should treat
// null as "skip the lookup". A page reload clears the cache, which matches the
// existing reload-on-org-switch model.

import { useRobotStore } from '@/stores/useRobotStore';
import { fetchOrgRobots } from './orgs-api';

const robotsByOrg = new Map<number, Promise<Map<string, number> | null>>();

export function loadRobotsForOrg(orgId: number): Promise<Map<string, number> | null> {
  const existing = robotsByOrg.get(orgId);
  if (existing) return existing;
  const p = fetchOrgRobots(orgId)
    .then((robots) => (robots ? new Map(robots.map((r) => [r.name, r.id])) : null))
    .catch(() => null);
  robotsByOrg.set(orgId, p);
  return p;
}

export async function resolveRobotIntId(orgId: number, robotKey: string): Promise<number | null> {
  const cached = useRobotStore.getState().fleet.find((r) => r.id === robotKey)?.hubId;
  if (cached != null) return cached;
  const byName = await loadRobotsForOrg(orgId);
  return byName?.get(robotKey) ?? null;
}
